import { Params } from '@angular/router';
import { PassedInitialConfig } from 'angular-auth-oidc-client';
import { environment } from 'src/environments/environment';

/**
 * Scopes description
 * -------------------------
 * openid (required):   default ID Token structure
 * profile:             basic profile informations (name, family_name, given_name...)
 * offline_access:      get a refresh token with the Auth0 response
 * email:               email informations
 * auth0-user-api-spa:  Auth0 api used (provided per-environment via customScope)
 */
const DEFAULT_SCOPE = 'openid profile offline_access email';

/**
 * Effective OIDC scope: the default scopes plus any environment- or
 * runtime-specific custom scopes (e.g. `auth0-user-api-spa`). When
 * `environment.auth0.customScope` is empty, only the default scopes are
 * requested.
 */
const scope = [DEFAULT_SCOPE, environment.auth0.customScope]
  .filter((part) => part.trim().length > 0)
  .join(' ');

/**
 * Query parameters forwarded from the entry URL to the Auth0 /authorize
 * request. Enables invitation-based onboarding, organization
 * auto-association, and other contextual SSO flows.
 */
export const AUTH_FORWARDED_QUERY_PARAMS = [
  'invitation',
  'organization',
  'organization_name',
] as const;

/**
 * Extracts the subset of {@link AUTH_FORWARDED_QUERY_PARAMS} present in the
 * given route query parameters, ready to be passed as `customParams` to the
 * Auth0 `/authorize` request. Empty/undefined values are skipped.
 *
 * @param queryParams The activated route query parameters.
 * @returns A record containing only the forwarded params that hold a value.
 */
export const pickForwardedAuthParams = (queryParams: Params): Record<string, string> => {
  const forwarded: Record<string, string> = {};
  for (const key of AUTH_FORWARDED_QUERY_PARAMS) {
    const value = queryParams[key];
    if (value) forwarded[key] = value;
  }
  return forwarded;
};

export const authConfig: PassedInitialConfig = {
  config: {
    authority: environment.auth0.authority,
    redirectUrl: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    clientId: environment.auth0.clientId,
    scope,
    responseType: 'code',
    silentRenew: true,
    useRefreshToken: true,
    renewTimeBeforeTokenExpiresInSeconds: 30,
    logLevel: environment.auth0.logLevel,
    customParamsAuthRequest: {
      audience: environment.auth0.audience,
    },
    customParamsRefreshTokenRequest: {
      scope,
    },
  },
};
